import { MouseEvent as ReactMouseEvent } from "react";

import { differenceInDays, differenceInMonths, differenceInYears, format } from "date-fns";

import { Tooltip } from "@/components/Tooltip/Tooltip";
import { TableColumn } from "@/components/Table/Table";
import { SortParams } from "@/types/types";

import { ContactProspect, ContactProspectEmployment } from "../schema/contacts";
import { getOrderStatus } from "./column-utils";

// ─── Person profile columns (parity with the list detail) ───────────────────
//
// These render functions live in their own file (not the 3k-line `allColumns.tsx`,
// which is over the 500-line ratchet). Each reads the `ContactProspect` row directly
// — the Contacts equivalent of the list's `row.prospect`.

const sortEmploymentsByStart = (a: ContactProspectEmployment, b: ContactProspectEmployment) =>
	new Date(b.startedAt ?? 0).getTime() - new Date(a.startedAt ?? 0).getTime();

/**
 * Employments whose organization differs from the prospect's current organization,
 * most recent first — the "previous employers" the list detail shows.
 */
const getPreviousEmployments = (row: ContactProspect): ContactProspectEmployment[] => {
	const currentOrg = row.organization?.name;

	return (row.prospectEmployments ?? [])
		.filter(
			(employer) =>
				!!currentOrg &&
				!!employer.organizationName &&
				!employer.organizationName.includes(currentOrg) &&
				!currentOrg.includes(employer.organizationName)
		)
		.sort(sortEmploymentsByStart);
};

/**
 * Employments whose title differs from the prospect's current title, most recent first.
 */
const getPreviousJobTitles = (row: ContactProspect): ContactProspectEmployment[] => {
	const currentTitle = row.title;

	return (row.prospectEmployments ?? [])
		.filter(
			(employer) =>
				!!currentTitle &&
				!!employer.title &&
				!employer.title.includes(currentTitle) &&
				!currentTitle.includes(employer.title)
		)
		.sort(sortEmploymentsByStart);
};

export const renderContactHeadlineColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "prospect.headline";

	return {
		title: ts("table.headline"),
		width: "fit-content",
		minWidth: "180px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "contactHeadline",
		hideable: true,
		moveable: true,
		show: false,
		render: (row: ContactProspect) => (
			<Tooltip
				content={<div className="min-w-[300px] whitespace-normal text-sm">{row.headline}</div>}
			>
				<button
					className="cursor-pointer max-w-[200px] overflow-hidden text-ellipsis text-sm text-gray-700"
					type="button"
					onClick={(e: ReactMouseEvent) => {
						e.preventDefault();
						e.stopPropagation();
						if (row.headline) {
							handleCopyClick(row.headline);
						}
					}}
				>
					{row.headline || "-"}
				</button>
			</Tooltip>
		),
	};
};

export const renderContactAboutColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "prospect.about";

	return {
		title: ts("table.about"),
		width: "fit-content",
		minWidth: "180px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "contactAbout",
		hideable: true,
		moveable: true,
		show: false,
		render: (row: ContactProspect) => (
			<Tooltip content={<div className="min-w-[300px] whitespace-normal text-sm">{row.about}</div>}>
				<button
					className="cursor-pointer max-w-[200px] overflow-hidden text-ellipsis text-sm text-gray-700"
					type="button"
					onClick={(e: ReactMouseEvent) => {
						e.preventDefault();
						e.stopPropagation();
						if (row.about) {
							handleCopyClick(row.about);
						}
					}}
				>
					{row.about || "-"}
				</button>
			</Tooltip>
		),
	};
};

export const renderContactLanguageColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "prospect.language";

	return {
		title: ts("table.language"),
		width: "fit-content",
		minWidth: "100px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "contactLanguage",
		hideable: true,
		moveable: true,
		show: false,
		render: (row: ContactProspect) => (
			<span className="text-sm text-gray-700">{row.language || "-"}</span>
		),
	};
};

export const renderContactConnectionsColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "prospect.connections";

	return {
		title: ts("table.connections"),
		width: "fit-content",
		minWidth: "110px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "contactConnections",
		hideable: true,
		moveable: true,
		show: false,
		render: (row: ContactProspect) => (
			<span className="text-sm text-gray-700">
				{typeof row.connections === "number" ? row.connections.toLocaleString() : "-"}
			</span>
		),
	};
};

export const renderContactFollowersColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "prospect.followers";

	return {
		title: ts("table.followers"),
		width: "fit-content",
		minWidth: "110px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "contactFollowers",
		hideable: true,
		moveable: true,
		show: false,
		render: (row: ContactProspect) => (
			<span className="text-sm text-gray-700">
				{typeof row.followers === "number" ? row.followers.toLocaleString() : "-"}
			</span>
		),
	};
};

export const renderContactReportedEmployeesColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "organization.employeesFrom";

	return {
		title: ts("table.reportedEmployees"),
		width: "fit-content",
		minWidth: "140px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "contactReportedEmployees",
		hideable: true,
		moveable: true,
		show: false,
		render: (row: ContactProspect) => {
			const { employeesFrom, employeesTo } = row.organization ?? {};

			if (employeesFrom == null && employeesTo == null) {
				return <span className="text-sm text-gray-700">-</span>;
			}

			const display =
				employeesTo != null && employeesTo !== employeesFrom
					? `${employeesFrom ?? ""}${employeesFrom != null ? " - " : ""}${employeesTo}`
					: `${employeesFrom ?? employeesTo}`;

			return (
				<button
					className="cursor-pointer text-sm text-gray-700"
					type="button"
					onClick={(e: ReactMouseEvent) => {
						e.preventDefault();
						e.stopPropagation();
						handleCopyClick(display);
					}}
				>
					{display}
				</button>
			);
		},
	};
};

export const renderContactRoleChangeColumn = (ts: (key: string) => string): TableColumn => {
	return {
		title: ts("table.roleChange"),
		width: "fit-content",
		minWidth: "160px",
		uid: "contactRoleChange",
		hideable: true,
		moveable: true,
		show: false,
		render: (row: ContactProspect) => {
			const startedAt = row.startedCurrentPosition ? new Date(row.startedCurrentPosition) : null;

			if (!startedAt || !row.jobChangeOrPromotion) {
				return <span className="text-sm text-gray-700">-</span>;
			}

			const years = differenceInYears(new Date(), startedAt);
			const months = differenceInMonths(new Date(), startedAt);
			const days = differenceInDays(new Date(), startedAt);

			let ago = "";

			if (years > 0) {
				ago = `${years}+ years ago`;
			} else if (months > 0) {
				ago = `${months}+ months ago`;
			} else if (days > 0) {
				ago = `${days}+ days ago`;
			}

			return (
				<Tooltip
					content={
						<div className="whitespace-normal text-sm">
							{row.jobChangeOrPromotion} in {format(startedAt, "MMMM yyyy")}
						</div>
					}
				>
					<span className="text-sm text-gray-700">
						{row.jobChangeOrPromotion} {ago}
					</span>
				</Tooltip>
			);
		},
	};
};

export const renderContactPreviousEmployersColumn = (ts: (key: string) => string): TableColumn => {
	return {
		title: ts("table.previousEmployers"),
		width: "fit-content",
		minWidth: "200px",
		uid: "contactPreviousEmployers",
		hideable: true,
		moveable: true,
		show: false,
		render: (row: ContactProspect) => {
			const previous = getPreviousEmployments(row);
			const latest = previous[0] ?? null;

			return (
				<Tooltip
					content={
						<div className="flex flex-col gap-2 text-sm">
							{previous.map((employer) => (
								<div key={`prev-emp-${employer.id}`}>
									{employer.title} @ {employer.organizationName}
								</div>
							))}
						</div>
					}
					disabled={previous.length === 0}
				>
					<span className="whitespace-nowrap text-sm text-gray-700">
						{latest ? `${latest.title} @ ${latest.organizationName}` : "-"}
					</span>
				</Tooltip>
			);
		},
	};
};

export const renderContactPreviousEmployerColumn = (ts: (key: string) => string): TableColumn => {
	return {
		title: ts("table.previousEmployer"),
		width: "fit-content",
		minWidth: "160px",
		uid: "contactPreviousEmployer",
		hideable: true,
		moveable: true,
		show: false,
		render: (row: ContactProspect) => {
			const previous = getPreviousEmployments(row);
			const latest = previous[0] ?? null;

			return (
				<Tooltip
					content={
						<div className="flex flex-col gap-2 text-sm">
							{previous.map((employer) => (
								<div key={`prev-employer-${employer.id}`}>{employer.organizationName}</div>
							))}
						</div>
					}
					disabled={previous.length === 0}
				>
					<span className="whitespace-nowrap text-sm text-gray-700">
						{latest?.organizationName || "-"}
					</span>
				</Tooltip>
			);
		},
	};
};

export const renderContactPreviousJobTitleColumn = (ts: (key: string) => string): TableColumn => {
	return {
		title: ts("table.previousJobTitle"),
		width: "fit-content",
		minWidth: "160px",
		uid: "contactPreviousJobTitle",
		hideable: true,
		moveable: true,
		show: false,
		render: (row: ContactProspect) => {
			const previous = getPreviousJobTitles(row);
			const latest = previous[0] ?? null;

			return (
				<Tooltip
					content={
						<div className="flex flex-col gap-2 text-sm">
							{previous.map((employer) => (
								<div key={`prev-title-${employer.id}`}>{employer.title}</div>
							))}
						</div>
					}
					disabled={previous.length === 0}
				>
					<span className="whitespace-nowrap text-sm text-gray-700">{latest?.title || "-"}</span>
				</Tooltip>
			);
		},
	};
};
